import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getSEOData } from '../utils/seoData';
import './Memory.css';

/**
 * Visual Memory dashboard — the JEPA side of Guru-EWM (ewm-ui/main.py: @ui.page("/memory")).
 *
 * Every call is proxied through Flask (`/api/nanolm/*` -> GATEWAY_URL) exactly like
 * the NanoLM chat, so the page also works when the gateway is only reachable from
 * the server (no CORS on the gateway itself).
 */

const GATEWAY_PROXY = '/api/nanolm';

const SET_OPERATIONS = ['union', 'intersection', 'difference', 'symmetric_difference'];

const ICONS = {
  library: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 4v16M3 9h4M3 15h4" />
    </>
  ),
  find: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </>
  ),
  newer: (
    <>
      <path d="M12 3l1.9 4.6L18.5 9l-4.6 1.9L12 15.5l-1.9-4.6L5.5 9l4.6-1.4z" />
      <path d="M18 17l.8 2L21 19.8l-2.2.8L18 23l-.8-2.4L15 19.8l2.2-.8z" />
    </>
  ),
  timeline: (
    <>
      <path d="M3 12h4M10 6h4M10 18h4M17 12h4" />
      <circle cx="8" cy="12" r="1.6" />
      <circle cx="16" cy="6" r="1.6" />
      <circle cx="16" cy="18" r="1.6" />
    </>
  ),
  combine: (
    <>
      <circle cx="9" cy="12" r="6" />
      <circle cx="15" cy="12" r="6" />
    </>
  ),
};

const TABS = [
  { id: 'library', label: 'Library' },
  { id: 'find', label: 'Find similar' },
  { id: 'new', label: "What's new" },
  { id: 'timeline', label: 'Timeline' },
  { id: 'combine', label: 'Combine' },
];

function TabIcon({ name, size = 16 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICONS[name]}
    </svg>
  );
}

// ── Gateway helpers ────────────────────────────────────────────────────
// Mirrors ewm-ui/main.py's _get() / _post_files(): a non-200 is reported as
// `{ error: ... }` rather than thrown, so callers only handle one shape.

function toError(e) {
  return String(e && e.message ? e.message : e);
}

async function apiGet(path) {
  try {
    const r = await fetch(`${GATEWAY_PROXY}${path}`);
    const data = await r.json().catch(() => ({}));
    if (!r.ok && !data.error) return { error: `HTTP ${r.status}` };
    return data;
  } catch (e) {
    return { error: toError(e) };
  }
}

async function apiPostFile(path, file, fields = {}) {
  const fd = new FormData();
  if (file) fd.append('file', file, file.name || 'file');
  Object.keys(fields).forEach((k) => fd.append(k, fields[k]));
  try {
    const r = await fetch(`${GATEWAY_PROXY}${path}`, { method: 'POST', body: fd });
    const data = await r.json().catch(() => ({}));
    if (!r.ok && !data.error) return { error: `HTTP ${r.status}` };
    return data;
  } catch (e) {
    return { error: toError(e) };
  }
}

/**
 * POST a plain (non-file) form. Used by /jepa/setop and /jepa/reconstruct, which
 * expect form fields rather than JSON. Pass `blob: true` for endpoints that
 * return image bytes (reconstruct).
 */
async function apiPostForm(path, fields, { blob = false } = {}) {
  try {
    const r = await fetch(`${GATEWAY_PROXY}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(fields).toString(),
    });
    if (blob) {
      if (!r.ok) return { error: (await r.text()) || `HTTP ${r.status}` };
      return { blob: await r.blob() };
    }
    const data = await r.json().catch(() => ({}));
    if (!r.ok && !data.error) return { error: `HTTP ${r.status}` };
    return data;
  } catch (e) {
    return { error: toError(e) };
  }
}

function similarityColor(pct) {
  if (pct >= 75) return '#22c55e';
  if (pct >= 40) return '#eab308';
  return '#94a3b8';
}

function shortId(id) {
  return String(id || 'clip').slice(0, 24);
}

function ClipThumb({ image, size = 140 }) {
  if (image) {
    return (
      <img
        src={image}
        alt="Memory clip"
        className="mem-thumb"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div className="mem-thumb mem-thumb--empty" style={{ width: size, height: size, fontSize: size / 2 }}>
      🎬
    </div>
  );
}

function Memory() {
  const [tab, setTab] = useState('library');
  const [encoder, setEncoder] = useState('');

  // Library
  const [clips, setClips] = useState([]);
  const [libraryStatus, setLibraryStatus] = useState('');
  const [libraryError, setLibraryError] = useState('');

  // Find similar
  const [findBusy, setFindBusy] = useState(false);
  const [findResult, setFindResult] = useState(null);

  // What's new (DRN)
  const [drnBusy, setDrnBusy] = useState(false);
  const [drnResult, setDrnResult] = useState(null);

  // Timeline
  const [timeline, setTimeline] = useState(null);
  const [timelineStatus, setTimelineStatus] = useState('');

  // Combine
  const [combineMemories, setCombineMemories] = useState([]);
  const [clipA, setClipA] = useState('');
  const [clipB, setClipB] = useState('');
  const [op, setOp] = useState('union');
  const [combineResult, setCombineResult] = useState(null);
  const [combineError, setCombineError] = useState('');

  // Regenerate dialog
  const [recon, setRecon] = useState(null);

  const addInputRef = useRef(null);
  const findInputRef = useRef(null);
  const drnInputRef = useRef(null);
  // Object URLs handed to <img>/download links must be revoked when replaced.
  const reconUrlsRef = useRef([]);

  const loadLibrary = useCallback(async () => {
    const data = await apiGet('/jepa/memories');
    if (data.error) {
      setLibraryError(`Error: ${data.error}`);
      return;
    }
    setLibraryError('');
    const memories = data.memories || [];
    setClips(memories);
    setLibraryStatus(
      `${memories.length} clip(s) in memory · encoder: ${data.encoder || '?'}`
    );
    setCombineMemories(memories);
    setClipA((prev) => prev || (memories[0] ? memories[0].content_key : ''));
    setClipB((prev) => prev || (memories.length ? memories[memories.length - 1].content_key : ''));
  }, []);

  const loadTimeline = useCallback(async () => {
    const [pyr, st] = await Promise.all([apiGet('/jepa/temporal'), apiGet('/jepa/status')]);
    const mem = st.memory || {};
    setTimelineStatus(
      `encoder: ${st.encoder || '?'} · memories: ${mem.memories || 0} · LUT: ${mem.lut_size || 0}`
    );
    setTimeline(pyr.pyramid || {});
  }, []);

  useEffect(() => {
    (async () => {
      const st = await apiGet('/jepa/status');
      if (!st.error) setEncoder(st.encoder || '');
      await loadLibrary();
      await loadTimeline();
    })();
  }, [loadLibrary, loadTimeline]);

  // Revoke any object URLs still alive when the page unmounts.
  useEffect(
    () => () => {
      reconUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      reconUrlsRef.current = [];
    },
    []
  );

  const onAddClip = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (addInputRef.current) addInputRef.current.value = '';
    if (!file) return;
    const res = await apiPostFile('/jepa/ingest', file, { name: file.name });
    if (res.error) setLibraryError(`Error: ${res.error}`);
    else setLibraryError('');
    await loadLibrary();
  };

  const onFind = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (findInputRef.current) findInputRef.current.value = '';
    if (!file) return;
    setFindBusy(true);
    const res = await apiPostFile('/jepa/query', file, { top_k: '10' });
    setFindBusy(false);
    setFindResult(res);
  };

  const onCheckNovelty = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (drnInputRef.current) drnInputRef.current.value = '';
    if (!file) return;
    setDrnBusy(true);
    const res = await apiPostFile('/jepa/drn', file);
    setDrnBusy(false);
    setDrnResult(res);
  };

  const onReconstruct = async (contentKey, name) => {
    reconUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    reconUrlsRef.current = [];
    setRecon({ open: true, name, loading: true, original: null, memory: null });

    const [orig, mem] = await Promise.all([
      apiPostForm('/jepa/reconstruct', { content_key: contentKey, mode: 'original' }, { blob: true }),
      apiPostForm('/jepa/reconstruct', { content_key: contentKey, mode: 'memory' }, { blob: true }),
    ]);

    const next = { open: true, name, loading: false, original: null, memory: null };
    if (orig.blob) {
      const url = URL.createObjectURL(orig.blob);
      reconUrlsRef.current.push(url);
      next.original = { url };
    } else {
      next.original = { error: 'Original not stored for this clip.' };
    }
    if (mem.blob) {
      const url = URL.createObjectURL(mem.blob);
      reconUrlsRef.current.push(url);
      next.memory = { url };
    } else {
      next.memory = { error: `Regeneration failed: ${mem.error || 'unknown error'}` };
    }
    setRecon(next);
  };

  const closeRecon = () => {
    reconUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    reconUrlsRef.current = [];
    setRecon(null);
  };

  const onSetOp = async () => {
    setCombineResult(null);
    if (!clipA || !clipB) {
      setCombineError('Select two clips first.');
      return;
    }
    setCombineError('');
    const res = await apiPostForm('/jepa/setop', {
      content_key_a: clipA,
      content_key_b: clipB,
      op,
    });
    if (res.error) {
      setCombineError(`Error: ${res.error}`);
      return;
    }
    setCombineResult(res);
  };

  const clipOptions = combineMemories.map((m) => ({
    value: m.content_key,
    label: m.id || String(m.content_key).slice(0, 12),
  }));

  const layerSizes = (timeline && timeline.layers) || [];
  const layerTotal = layerSizes.reduce((a, b) => a + b, 0);

  const findMatches = findResult && !findResult.error ? findResult.matches || [] : [];
  const drn = drnResult && !drnResult.error ? drnResult.drn || {} : null;

  const stats = (combineResult && combineResult.stats) || null;

  return (
    <div className="memory-page">
      <SEO {...getSEOData('memory')} />

      <header className="memory-header">
        <div className="memory-header-inner">
          <div className="memory-brand">
            <span className="memory-logo">🧠</span>
            <div className="memory-brand-text">
              <span className="memory-brand-title">Visual Memory</span>
              <span className="memory-brand-subtitle">
                JEPA world-model memory{encoder ? ` · ${encoder}` : ''}
              </span>
            </div>
          </div>
          <Link to="/nanolm" className="memory-back">
            ← Back to chat
          </Link>
        </div>
      </header>

      <nav className="memory-tabs" role="tablist" aria-label="Visual memory views">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`memory-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <TabIcon name={t.id} />
            {t.label}
          </button>
        ))}
      </nav>

      <div className="memory-panel">
        {/* ── Library ─────────────────────────────────────────────── */}
        {tab === 'library' && (
          <section>
            <p className="memory-subtitle">{libraryStatus}</p>
            <div className="memory-toolbar">
              <input ref={addInputRef} type="file" hidden onChange={onAddClip} />
              <button
                className="memory-btn memory-btn--primary"
                onClick={() => addInputRef.current && addInputRef.current.click()}
              >
                + Add clip to memory
              </button>
              <button className="memory-btn" onClick={loadLibrary}>
                ⟳ Refresh
              </button>
            </div>

            {libraryError && <p className="memory-error">{libraryError}</p>}

            {clips.length === 0 ? (
              <p className="memory-empty">Nothing in memory yet — add a clip above.</p>
            ) : (
              <div className="memory-grid">
                {clips.map((m) => (
                  <div className="memory-card" key={m.content_key || m.id}>
                    <ClipThumb image={m.image} size={160} />
                    <span className="memory-card-title">{shortId(m.id)}</span>
                    <span className="memory-card-meta">
                      {m.n_patches || 0} tokens · {m.unique_tids || 0} unique
                    </span>
                    <button
                      className="memory-btn memory-btn--ghost"
                      onClick={() => onReconstruct(m.content_key, m.id || 'clip')}
                    >
                      ⟲ Regenerate
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── Find similar ────────────────────────────────────────── */}
        {tab === 'find' && (
          <section>
            <div className="memory-toolbar">
              <input ref={findInputRef} type="file" hidden onChange={onFind} />
              <button
                className="memory-btn memory-btn--primary"
                disabled={findBusy}
                onClick={() => findInputRef.current && findInputRef.current.click()}
              >
                ⬆ Upload a clip to match
              </button>
            </div>

            {findBusy && <p className="memory-subtitle">Matching…</p>}
            {findResult && findResult.error && (
              <p className="memory-error">Error: {findResult.error}</p>
            )}
            {findResult && !findResult.error && findMatches.length === 0 && (
              <p className="memory-empty">No matches — ingest a clip first, then search.</p>
            )}

            {findMatches.map((m, i) => {
              const pct = Math.round((parseFloat(m.jaccard) || 0) * 100);
              return (
                <div className="memory-card memory-card--row" key={i}>
                  <ClipThumb image={m.image} size={96} />
                  <div className="memory-card-body">
                    <span className="memory-card-title">{m.id || 'clip'}</span>
                    <div className="memory-bar-track">
                      <div
                        className="memory-bar-fill"
                        style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: similarityColor(pct) }}
                      />
                    </div>
                    <span className="memory-bar-label">{pct}% similar</span>
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {/* ── What's new (DRN) ────────────────────────────────────── */}
        {tab === 'new' && (
          <section>
            <div className="memory-toolbar">
              <input ref={drnInputRef} type="file" hidden onChange={onCheckNovelty} />
              <button
                className="memory-btn memory-btn--primary"
                disabled={drnBusy}
                onClick={() => drnInputRef.current && drnInputRef.current.click()}
              >
                ✦ Check a clip for novelty
              </button>
            </div>

            {drnBusy && <p className="memory-subtitle">Analysing…</p>}
            {drnResult && drnResult.error && <p className="memory-error">Error: {drnResult.error}</p>}

            {drn && (
              <>
                <div className="memory-stats">
                  <div className="memory-stat">
                    <span className="memory-stat-label">🆕 Novel</span>
                    <span className="memory-stat-value">{drn.n_size || 0}</span>
                  </div>
                  <div className="memory-stat">
                    <span className="memory-stat-label">✅ Retained</span>
                    <span className="memory-stat-value">{drn.r_size || 0}</span>
                  </div>
                  <div className="memory-stat">
                    <span className="memory-stat-label">⬅️ Departed</span>
                    <span className="memory-stat-value">{drn.d_size || 0}</span>
                  </div>
                </div>
                <p className="memory-bar-label">
                  Memory context: {drnResult.context_size || 0} patterns · pyramid{' '}
                  {drnResult.pyramid_layers}
                </p>
              </>
            )}
          </section>
        )}

        {/* ── Timeline ────────────────────────────────────────────── */}
        {tab === 'timeline' && (
          <section>
            <p className="memory-subtitle">{timelineStatus}</p>
            <div className="memory-toolbar">
              <button className="memory-btn" onClick={loadTimeline}>
                ⟳ Refresh
              </button>
            </div>

            <div className="memory-pyramid">
              {layerSizes.map((sz, i) => (
                <div className="memory-pyramid-col" key={i}>
                  <div
                    className="memory-pyramid-bar"
                    style={{ height: `${Math.max(8, Math.floor((120 * sz) / Math.max(layerTotal, 1)))}px` }}
                  />
                  <span className="memory-bar-label">L{i}</span>
                </div>
              ))}
            </div>

            <p className="memory-bar-label">
              Temporal pyramid: {layerSizes.length} layers · {layerTotal} patterns ·{' '}
              {(timeline && timeline.steps) || 0} step(s) · context{' '}
              {(timeline && timeline.context_size) || 0}
            </p>
            {timeline && timeline.tf_time_lens && (
              <p className="memory-bar-label">Time lens: {timeline.tf_time_lens}</p>
            )}
          </section>
        )}

        {/* ── Combine ─────────────────────────────────────────────── */}
        {tab === 'combine' && (
          <section>
            <p className="memory-subtitle">
              {combineMemories.length} clip(s) available to combine
            </p>

            <div className="memory-toolbar">
              <label className="memory-field">
                <span>Clip A</span>
                <select value={clipA} onChange={(e) => setClipA(e.target.value)}>
                  {clipOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="memory-field">
                <span>Clip B</span>
                <select value={clipB} onChange={(e) => setClipB(e.target.value)}>
                  {clipOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="memory-field">
                <span>Operation</span>
                <select value={op} onChange={(e) => setOp(e.target.value)}>
                  {SET_OPERATIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </label>
              <button className="memory-btn memory-btn--primary" onClick={onSetOp}>
                ⧉ Compute
              </button>
            </div>

            {combineError && <p className="memory-error">{combineError}</p>}

            {stats && (
              <p className="memory-bar-label">
                {op}: |A|={stats.a_unique || 0} · |B|={stats.b_unique || 0} · ∪=
                {stats.union || 0} · ∩={stats.intersection || 0} · A−B={stats.difference || 0} ·
                Jaccard={stats.jaccard || 0}
              </p>
            )}

            {combineResult && combineResult.image && (
              <div className="memory-combine-out">
                <img src={combineResult.image} alt={`${op} result`} className="memory-combine-img" />
                <a
                  className="memory-btn memory-btn--primary"
                  href={combineResult.image}
                  download={`${op}.png`}
                >
                  ⬇ Download
                </a>
              </div>
            )}
          </section>
        )}
      </div>

      {recon && recon.open && (
        <div className="memory-modal-backdrop" onClick={closeRecon} role="presentation">
          <div
            className="memory-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Regenerated from HLLSet storage"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="memory-modal-title">Regenerated from HLLSet storage</h2>

            {recon.loading ? (
              <p className="memory-subtitle">Decoding…</p>
            ) : (
              <>
                <p className="memory-modal-label">Original (exact)</p>
                {recon.original && recon.original.url ? (
                  <>
                    <img src={recon.original.url} alt="Original clip" className="memory-modal-img" />
                    <a
                      className="memory-btn memory-btn--primary"
                      href={recon.original.url}
                      download={recon.name}
                    >
                      ⬇ Download
                    </a>
                  </>
                ) : (
                  <p className="memory-muted">Original not stored for this clip.</p>
                )}

                <p className="memory-modal-label">From memory (lossy)</p>
                {recon.memory && recon.memory.url ? (
                  <>
                    <img src={recon.memory.url} alt="Reconstructed clip" className="memory-modal-img" />
                    <a
                      className="memory-btn memory-btn--primary"
                      href={recon.memory.url}
                      download={`${recon.name}_reconstructed.png`}
                    >
                      ⬇ Download
                    </a>
                  </>
                ) : (
                  <p className="memory-error">
                    {recon.memory ? recon.memory.error : 'Regeneration failed.'}
                  </p>
                )}
              </>
            )}

            <button className="memory-btn memory-modal-close" onClick={closeRecon}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Memory;
